import { Logger } from '@gitbook/util-debug';
import { isPromise } from '@gitbook/util-ts';

import { getPaletteId } from './id';
import type { PaletteAnchorRef, PaletteCommandDef, PaletteDef } from './types';

const logger = Logger('palette');

/**
 * Create a palette from scratch.
 */
export function createPalette(props: Partial<PaletteDef>): PaletteDef {
    return {
        groups: [],
        query: {
            text: props.search?.initialValue ?? '',
        },
        ...props,
    };
}

/**
 * Execute a command and returns the new palette if any.
 */
export function executeCommandInPalette(
    palette: PaletteDef,
    command: string | PaletteCommandDef
): PaletteDef | null | Promise<PaletteDef | null> {
    const commandInst =
        typeof command === 'string' ? resolveCommandInPalette(palette, command) : command;

    if (!commandInst) {
        logger.warning(`Command not found "${command}"`);
        return null;
    }

    if (commandInst.trigger) {
        const result = commandInst.trigger(palette);
        function setPosition(result: PaletteDef | void): PaletteDef | null {
            if (!result) {
                return null;
            }

            return {
                ...result,
                // Merge the position with the current one.
                position: palette.position
                    ? {
                          ...palette.position,
                          width: result.position?.width ?? palette.position.width,
                          maxHeight: result.position?.maxHeight ?? palette.position.maxHeight,
                      }
                    : result.position,
                // Preserve positioning from the current palette when available
                parent: palette.parent && !result.parent ? palette.parent : result.parent,
            };
        }

        if (!isPromise(result)) {
            return setPosition(result);
        }

        return result.then(setPosition);
    }

    return null;
}

/**
 * Open the descendant palette of a command.
 */
export function openDescendantInPalette(
    palette: PaletteDef,
    command: PaletteCommandDef,
    commandRef: PaletteAnchorRef
): PaletteDef | null {
    if (command.descendant) {
        return {
            ...palette,
            descendant: {
                palette: {
                    ...command.descendant(),
                    parent: palette,
                },
                anchorRef: commandRef,
                id: getPaletteId(commandRef),
            },
        };
    }

    return null;
}

/**
 * Resolve a command by its ID in a palette.
 */
function resolveCommandInPalette(
    palette: PaletteDef,
    commandKey: string
): PaletteCommandDef | null {
    for (const group of palette.groups) {
        if (Array.isArray(group.commands)) {
            for (const command of group.commands) {
                if (command.key === commandKey) {
                    return command;
                }
            }
        }
    }

    return null;
}

/**
 * Transform to type a text in a palette query.
 */
export function typeQueryInPalette(palette: PaletteDef, text: string): PaletteDef {
    return {
        ...palette,
        query: {
            ...palette.query,
            text,
        },
        descendant: undefined,
    };
}

/**
 * Return true if a palette should have a search input.
 */
export function hasPaletteSearchInput(palette: PaletteDef): boolean {
    return !!palette.search;
}
